/**
 * Transactions Tabs Component
 * 
 * Consolidates all transaction-related functionality into a single tabbed interface:
 * - Transactions: Main transaction review and correction
 * - Upload: File upload and processing
 * - Validation: Transaction validation workflow
 * - Review Queue: AI suggestions review
 * 
 * This replaces the need for separate FileProcessor, Validation, and ReviewQueue pages.
 */

import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FilterPanel } from "@/components/transactions/filter-panel";
import { TransactionGrid } from "@/components/transactions/transaction-grid";
import { LearningSuggestionsPanel } from "@/components/transactions/learning-suggestions-panel";
import { QuickCorrectionPanel } from "@/components/transactions/quick-correction-panel";
import { KeyboardShortcutsDialog } from "@/components/transactions/keyboard-shortcuts-dialog";
import { BulkCorrectionPanel } from "@/components/transactions/bulk-correction-panel";
import { FileUploadZone } from "@/components/file-processor/file-upload-zone";
import { ProcessingQueue } from "@/components/file-processor/processing-queue";
import { AiProcessingAnalysis } from "@/components/file-processor/ai-processing-analysis";
import { UploadHistory } from "@/components/file-processor/upload-history";
import { FocusedMode } from "@/components/transactions/focused-mode";
import { WorkflowGuidanceCard } from "@/components/transactions/workflow-guidance-card";
import { SmartFilterChips } from "@/components/transactions/smart-filter-chips";
import { JournalTab } from "@/components/transactions/journal-tab";
import { useAuth } from "@/contexts/auth-context";
import { useActiveMandate } from "@/hooks/use-active-mandate";
import { useTransactions } from "@/hooks/use-transactions";
import { useToast } from "@/hooks/use-toast";
import { useValidationStats } from "@/hooks/use-validation-stats";
import type { TransactionFilters } from "@/services/transactions";

interface TransactionsTabsProps {
  className?: string;
}

export const TransactionsTabs: React.FC<TransactionsTabsProps> = ({ className }) => {
  const { organization } = useAuth();
  const { mandate } = useActiveMandate();
  const { toast } = useToast();
  const { data: validationStats } = useValidationStats();
  
  // State for main processing tab
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [sortBy, setSortBy] = useState<'confidence' | 'date' | 'amount'>('confidence');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showQuickCorrection, setShowQuickCorrection] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showBulkCorrection, setShowBulkCorrection] = useState(false);
  
  // State for Focused Mode
  const [focusedModeActive, setFocusedModeActive] = useState(false);
  const [focusedModeTransactions, setFocusedModeTransactions] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  // State for Upload tab
  const [refreshKey, setRefreshKey] = useState(0);
  
  const selectedCount = selectedIds.length;
  const organizationId = organization?.id;
  const mandateId = mandate?.id;

  // Fetch real transaction data for counters
  const { data: transactionData } = useTransactions({
    page: 1,
    pageSize: 5000, // Increased limit to show all transactions
    filters,
    enabled: !!mandateId,
  });

  // Ensure the current mandate filters the transaction grid
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      mandateId: mandateId || undefined
    }));
  }, [mandateId]);

  // Calculate transaction counts by confidence from real data
  const realTransactions = transactionData?.transactions || [];
  const highConfidenceCount = realTransactions.filter(t => (t.ai_confidence_score || 0) >= 90).length;
  const mediumConfidenceCount = realTransactions.filter(t => (t.ai_confidence_score || 0) >= 70 && (t.ai_confidence_score || 0) < 90).length;
  const lowConfidenceCount = realTransactions.filter(t => (t.ai_confidence_score || 0) < 70).length;

  // Filter chips configuration
  const filterChips = [
    {
      id: 'high',
      label: 'High Confidence',
      count: highConfidenceCount,
      variant: 'default' as const,
      icon: 'fa-solid fa-check-circle',
      color: '#22c55e'
    },
    {
      id: 'medium',
      label: 'Needs Review',
      count: mediumConfidenceCount,
      variant: 'secondary' as const,
      icon: 'fa-solid fa-eye',
      color: '#f59e0b'
    },
    {
      id: 'low',
      label: 'Fix Errors',
      count: lowConfidenceCount,
      variant: 'destructive' as const,
      icon: 'fa-solid fa-exclamation-triangle',
      color: '#ef4444'
    }
  ];

  const handleUploadComplete = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleStartFocusedMode = () => {
    const highConfidenceTransactions = realTransactions
      .filter(t => (t.ai_confidence_score || 0) >= 90)
      .map(t => ({
        id: t.id,
        date: t.transaction_date,
        amount: t.amount,
        counterparty: t.counterparty_name || t.description || 'Unknown',
        description: t.description,
        confidence: Math.round(t.ai_confidence_score || 0),
        debitAccount: t.debit_account || '1010',
        creditAccount: t.credit_account || '3200',
        vatRate: t.swiss_vat_rate || 0
      }));

    setFocusedModeTransactions(highConfidenceTransactions);
    setFocusedModeActive(true);
  };

  // Apply SmartFilterChips to grid filters
  useEffect(() => {
    if (activeFilter === 'high') {
      setFilters(prev => ({ ...prev, minConfidence: 90 }));
    } else if (activeFilter === 'medium') {
      setFilters(prev => ({ ...prev, minConfidence: 70, maxConfidence: 89 }));
    } else if (activeFilter === 'low') {
      setFilters(prev => ({ ...prev, maxConfidence: 69 }));
    } else {
      setFilters(prev => ({ ...prev, minConfidence: undefined, maxConfidence: undefined }));
    }
  }, [activeFilter]);

  const handleTransactionSelect = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowQuickCorrection(true);
  };

  const handleApplySuggestion = async (rule: any) => {
    if (!selectedTransaction) return;

    try {
      toast({
        title: 'Regel angewendet',
        description: `"${rule.pattern_key}" wurde auf die Transaktion angewendet.`,
      });
      setShowQuickCorrection(false);
      setSelectedTransaction(null);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Regel konnte nicht angewendet werden.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveCorrection = async (correction: any) => {
    try {
      toast({
        title: 'Korrektur gespeichert',
        description: 'Transaktion wurde erfolgreich korrigiert.',
      });
      setShowQuickCorrection(false);
      setSelectedTransaction(null);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Korrektur konnte nicht gespeichert werden.',
        variant: 'destructive',
      });
    }
  };

  const handleCancelCorrection = () => {
    setShowQuickCorrection(false);
    setSelectedTransaction(null);
  };

  const handleBulkCorrection = async (transactionIds: string[], updates: any) => {
    try {
      const promises = transactionIds.map(id => Promise.resolve());
      await Promise.all(promises);
      
      toast({
        title: 'Bulk-Korrektur erfolgreich',
        description: `${transactionIds.length} Transaktionen wurden korrigiert`,
      });
      
      setSelectedIds([]);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Bulk-Korrektur konnte nicht angewendet werden',
        variant: 'destructive',
      });
    }
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === '?') {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }
      // Also allow F1 for help
      if (e.key === 'F1') {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }
      // Focused Mode shortcut
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        handleStartFocusedMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [highConfidenceCount]);

  return (
    <div className={className}>
      <Tabs defaultValue="process" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="process" className="flex items-center gap-2">
            <i className="fa-solid fa-bolt"></i>
            Verarbeiten
            <div className="ml-2 flex gap-1">
              {highConfidenceCount > 0 && (
                <Badge variant="default" className="text-xs">
                  {highConfidenceCount}
                </Badge>
              )}
              {mediumConfidenceCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {mediumConfidenceCount}
                </Badge>
              )}
              {lowConfidenceCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {lowConfidenceCount}
                </Badge>
              )}
            </div>
          </TabsTrigger>
          
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <i className="fa-solid fa-upload"></i>
            Import
          </TabsTrigger>
          
          <TabsTrigger value="journal" className="flex items-center gap-2">
            <i className="fa-solid fa-book"></i>
            Journal
            <Badge variant="secondary" className="ml-1">
              12 gebucht
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Main Processing Tab */}
            <TabsContent value="process" className="space-y-4">
          {/* Workflow Guidance Card */}
          <WorkflowGuidanceCard
            highConfidenceCount={highConfidenceCount}
            mediumConfidenceCount={mediumConfidenceCount}
            lowConfidenceCount={lowConfidenceCount}
            onStartFocusedMode={handleStartFocusedMode}
          />

          {/* Smart Filter Chips */}
          <SmartFilterChips
            filters={filterChips}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main Transaction Grid */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <i className="fa-solid fa-list-check text-brand-primary"></i>
                      Transaktionen
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="lg"
                        onClick={handleStartFocusedMode}
                        className="flex items-center gap-2"
                        disabled={highConfidenceCount === 0}
                      >
                        <i className="fa-solid fa-bolt"></i>
                        🎯 Focused Mode (F)
                        {highConfidenceCount > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {highConfidenceCount} bereit
                          </Badge>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowKeyboardShortcuts(true)}
                        className="flex items-center gap-2"
                      >
                        <i className="fa-solid fa-keyboard text-xs"></i>
                        Tastenkürzel
                      </Button>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowBulkCorrection(true)}
                          className="flex items-center gap-2"
                        >
                          <i className="fa-solid fa-edit text-xs"></i>
                          Bulk ({selectedIds.length})
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <FilterPanel
                    onSortChange={setSortBy}
                  />
                  <div className="mt-4">
                    <TransactionGrid
                      filters={filters}
                      sortBy={sortBy}
                      onSelectionChange={setSelectedIds}
                      onTransactionSelect={handleTransactionSelect}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Learning Suggestions Panel */}
            <div className="lg:col-span-1">
              {selectedTransaction ? (
                <LearningSuggestionsPanel
                  transactionData={selectedTransaction}
                  onApplySuggestion={handleApplySuggestion}
                  className="sticky top-6"
                />
              ) : (
                <Card className="sticky top-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <i className="fa-solid fa-brain text-brand-primary"></i>
                      KI-Empfehlungen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                      <i className="fa-solid fa-mouse-pointer text-2xl mb-3"></i>
                      <p className="text-sm mb-2">
                        Wählen Sie eine Transaktion aus, um KI-Empfehlungen zu sehen
                      </p>
                      <div className="text-xs space-y-1">
                        <p>💡 <strong>Tipp:</strong> Verwenden Sie die Pfeiltasten zur Navigation</p>
                        <p>⌨️ <strong>Shortcut:</strong> Ctrl+? für alle Tastenkürzel</p>
                        <p>🎯 <strong>Focused Mode:</strong> Drücken Sie F für schnelle Verarbeitung</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-upload text-brand-primary"></i>
                  Datei Upload
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FileUploadZone
                  organizationId={organizationId}
                  mandateId={mandateId}
                  onUploadComplete={handleUploadComplete}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-clock text-brand-primary"></i>
                  Verarbeitungswarteschlange
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ProcessingQueue
                  organizationId={organizationId}
                  mandateId={mandateId}
                  refreshKey={refreshKey}
                />
              </CardContent>
            </Card>
          </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-brain text-brand-primary"></i>
                  KI-Analyse
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AiProcessingAnalysis
                  organizationId={organizationId}
                  mandateId={mandateId}
                  refreshKey={refreshKey}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <i className="fa-solid fa-history text-brand-primary"></i>
                  Upload-Verlauf
                </CardTitle>
              </CardHeader>
              <CardContent>
                <UploadHistory
                  organizationId={organizationId}
                  mandateId={mandateId}
                  refreshKey={refreshKey}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Journal Tab */}
            <TabsContent value="journal" className="space-y-4">
          <JournalTab />
        </TabsContent>

        {/* Focused Mode Overlay */}
        {focusedModeActive && (
          <FocusedMode
            transactions={focusedModeTransactions}
            onExit={() => setFocusedModeActive(false)}
            onComplete={() => {
              setFocusedModeActive(false);
              // Refresh transaction list
              toast({
                title: 'Focused Mode abgeschlossen',
                description: `${focusedModeTransactions.length} Transaktionen wurden verarbeitet`,
              });
            }}
          />
        )}
      </Tabs>

      {/* Modals and Overlays */}
      {showQuickCorrection && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <QuickCorrectionPanel
              transaction={selectedTransaction}
              onSave={handleSaveCorrection}
              onCancel={handleCancelCorrection}
              className="border-0 shadow-none"
            />
          </div>
        </div>
      )}

      {showBulkCorrection && selectedCount > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <BulkCorrectionPanel
              selectedTransactions={selectedIds.map(id => ({ id }))}
              onClose={() => setShowBulkCorrection(false)}
              onApplyCorrection={handleBulkCorrection}
              className="border-0 shadow-none"
            />
          </div>
        </div>
      )}

      <KeyboardShortcutsDialog
        open={showKeyboardShortcuts}
        onOpenChange={setShowKeyboardShortcuts}
      />

      {/* Floating Action Button for Bulk Operations */}
      {selectedCount > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setShowBulkCorrection(true)}
              className="bg-blue-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              title="Bulk-Korrektur öffnen"
            >
              <i className="fa-solid fa-bolt"></i>
              <span className="font-medium">{selectedCount} korrigieren</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
